import * as React from "react";
import * as moment from "moment";
import {
    Policy,
    POLICY_STATUS_MTC,
    POLICY_STATUS_PENDING_RENEWAL,
    Renewal,
    RENEWAL_PROPOSAL_EDIT_VIEW_JOURNEY_URL,
    resourcesHelper,
    Subscription
} from "@falcon/services";
import {ImgTileLink} from "../../../../../components/ImgTileLink";
import {Link, RouteComponentProps} from "react-router-dom";

interface IAnnualTilesProps extends RouteComponentProps {
    policies?: Policy[];
    subscription?: Subscription;
    renewalProposal?: Renewal;
    policyNumber: string;
    hasExpiredPolicies?: boolean;
    hasOldPolicies?: boolean;
    hasMultiplePolicies?: boolean;
    renewalEnabled: boolean;
    hasOldHomePolicies?: boolean;
}

function prepareFlags(policies: Policy[] | undefined, renewalProposal: Renewal | undefined) {
    const hasPolicies = policies && policies.length > 0;
    const hasPendingRenewal: boolean = renewalProposal != null && renewalProposal.status === POLICY_STATUS_PENDING_RENEWAL;
    const policy: Policy | null = policies && policies.length > 0 ? policies[0] : null;
    const hasActivePolicy: boolean = policy != null && moment.utc(policy.endDate, moment.ISO_8601).isSameOrAfter(moment(), 'day');
    const policyCanceled = policy != null && policy.status === POLICY_STATUS_MTC;
    const renewalStatus: object = {
        "status": hasPendingRenewal ? "Existing" : undefined
    };
    const inGracePeriod: boolean = policy?.annualPolicyInfo ? policy.annualPolicyInfo.inGracePeriod : false;
    const futureCancelledPolicyBeforeInception: boolean = policy != null && moment.utc(policy.endDate, moment.ISO_8601).isSame(moment.utc(policy.startDate, moment.ISO_8601))
        && moment.utc(policy.startDate, moment.ISO_8601).isAfter(moment(), 'day');
    return {
        hasPolicies,
        hasPendingRenewal,
        policy,
        hasActivePolicy,
        policyCanceled,
        renewalStatus,
        inGracePeriod,
        futureCancelledPolicyBeforeInception
    };
}

export const AnnualTiles: React.FC<IAnnualTilesProps> = (props) => {
    const {
        policies,
        hasExpiredPolicies,
        hasOldPolicies,
        subscription,
        renewalProposal,
        policyNumber,
        hasMultiplePolicies,
        hasOldHomePolicies
    } = props;

    const {
        hasPolicies,
        hasPendingRenewal,
        policy,

        hasActivePolicy,
        policyCanceled,
        renewalStatus,
        inGracePeriod,
        futureCancelledPolicyBeforeInception
    } = prepareFlags(policies, renewalProposal);

    const allianzAdvantagesEnabled: boolean = process.env.REACT_APP_ALLIANZ_ADVANTAGES_ENABLED ? JSON.parse(process.env.REACT_APP_ALLIANZ_ADVANTAGES_ENABLED) : false;

    return (
        <div className="link-grid">
            <div className="d-flex flex-wrap">

                {hasPendingRenewal && (
                    <div className="flexItem pending-renewal">
                        <ImgTileLink src={inGracePeriod ? `/${resourcesHelper.THEME}/img/pending_renewal_gray.svg` : `/${resourcesHelper.THEME}/img/pending_renewal.png`}
                                     alt="Policy renewal icon"
                                     to={RENEWAL_PROPOSAL_EDIT_VIEW_JOURNEY_URL + renewalProposal!._id}
                                     status="New"
                                     hasInfoButton={true}
                                     linkDisabled={inGracePeriod}
                                     backButtonName={inGracePeriod ? "Cancel" : "Back"}
                                     infoModalButtonHidden={inGracePeriod}
                                     infoModalTitle="Your renewal, make changes"
                                     infoModalAdditionalDescription={inGracePeriod ? <div className="banner-text banner-warn">
                                         <div className="banner-text">
                                             <p className="font-weight-bold">You are no longer covered</p>
                                             <p>You must renew your car policy before making changes to your cover</p>
                                                 <Link className="color-cyan-blue"
                                                       to={RENEWAL_PROPOSAL_EDIT_VIEW_JOURNEY_URL + renewalProposal!._id}>
                                                     <p className="font-weight-bold hyperlink mt-2 mb-2">
                                                         <img alt="Arrow icon" src={`/${resourcesHelper.THEME}/img/arrow-right.svg`}/>Renew your policy now
                                                     </p>
                                                 </Link>

                                         </div>
                                     </div> : ""}
                                     infoModalBulletPoints={["Make changes to your policy for next year (e.g. add a driver).",
                                         "Choose to pay in one go or monthly (if available).",
                                         "Add or remove optional extras for next year (e.g. breakdown cover).",
                                         "Review your level of cover and compare options (if eligible)."]}
                                     {...props}>
                            Your renewal, make changes
                        </ImgTileLink>
                    </div>
                )}

                {!futureCancelledPolicyBeforeInception && !(!hasActivePolicy && policyCanceled) && (
                    <div className="flexItem policy-summary">
                        <ImgTileLink src={`/${resourcesHelper.THEME}/img/policy-summary.png`}
                                     alt="Policy summary icon"
                                     to={`/dashboard/annualMotor/policySummary/${policyNumber}`}
                                     hasInfoButton={true}
                                     infoModalTitle="Your car policy"
                                     infoModalBulletPoints={["View a short breakdown of your car policy"]}
                                     infoModalButton="Policy summary"
                                     {...renewalStatus}
                                     {...props}>
                            Policy summary
                        </ImgTileLink>
                    </div>
                )}

                {!inGracePeriod && hasPolicies && !policyCanceled && (
                    <div className="flexItem car">
                        <ImgTileLink id="carTile"
                                     src={`/${resourcesHelper.THEME}/img/wolf-your-car.png`}
                                     alt="Car icon"
                                     to={`/dashboard/annualMotor/yourCars/${policyNumber}`}
                                     hasInfoButton={true}
                                     infoModalTitle="Your car, make changes"
                                     infoModalBulletPoints={["Change your car or edit the details", "Edit driver details",
                                         "Add/remove additional drivers", "Make changes to your policy (e.g. Business user, mileage etc.)",
                                         "Add or remove optional extras (e.g. Breakdown cover)"]}
                                     infoModalAdditionalDescription={
                                         <h4 className="mb-3 font-weight-400">
                                             With Allianz, you're in control. Because you make changes to your policy
                                             yourself,
                                             there are <strong>no admin fees</strong>! Remember, though, any changes you
                                             make can still change your
                                             price.
                                         </h4>}
                                     {...renewalStatus}
                                     {...props}>
                            Your car, make changes
                        </ImgTileLink>
                    </div>
                )}

                {hasPolicies && <div className="flexItem payment">
                    <ImgTileLink id="paymentTile"
                                 src={`/${resourcesHelper.THEME}/img/payment-card-icon.png`}
                                 alt="Payment icon"
                                 to={`/dashboard/annualPaymentHistory/${policyNumber}`}
                                 notification={hasPolicies && !policyCanceled && subscription?.nextPaymentAmount != null}
                                 hasInfoButton={true}
                                 infoModalTitle="Payment"
                                 infoModalBulletPoints={["Change your payment day", "Edit your monthly payment details",
                                     "Change your payment card", "Pay off your balance",
                                     "View payment history"]}
                                 {...props}>
                        Payment
                    </ImgTileLink>
                </div>}

                {hasPolicies && <div className="flexItem documents">
                    <ImgTileLink id="policyDocumentsTile"
                                 src={`/${resourcesHelper.THEME}/img/documents-icon.png`}
                                 alt="Documents icon"
                                 to={`/dashboard/annualMotorDocuments/${policyNumber}`}
                                 hasInfoButton={true}
                                 infoModalTitle="Documents"
                                 infoModalBulletPoints={["View your policy documents", "View your old policy documents",
                                     "Request documents to be sent by post"]}
                                 {...props}>
                        Documents
                    </ImgTileLink>
                </div>}

                {hasPolicies && <div className="flexItem make-a-claim">
                    <ImgTileLink id="claimsTile"
                                 src={`/${resourcesHelper.THEME}/img/make-a-claim.png`}
                                 alt="Make a claim icon"
                                 to={`/dashboard/pickAnnualMotorClaimKind/${policyNumber}`}
                                 hasInfoButton={true}
                                 infoModalTitle="Make a claim"
                                 infoModalBulletPoints={["Make a windscreen or window claim",
                                     "Make a breakdown claim",
                                     "Make other claims (road traffic accident, theft, stolen keys etc.)"]}
                                 {...props}>
                        Make a claim
                    </ImgTileLink>
                </div>}

                <div className="flexItem quotes">
                    <ImgTileLink id="getQuoteTile"
                                 src={`/${resourcesHelper.THEME}/img/savings_icon.png`}
                                 alt="Products icon"
                                 to={`/dashboard/getNewQuote`}
                                 routeState={{
                                     policy,
                                 }}
                                 hasInfoButton={true}
                                 infoModalTitle="Allianz products"
                                 infoModalBulletPoints={["View your car or home insurance quote", "Get a new car or home insurance quote",
                                     "Get a new LV= travel or LV= pet insurance quote"]}
                                 infoModalButton="More about Allianz products"
                                 {...props}>
                        Our products
                    </ImgTileLink>
                </div>

                {!hasActivePolicy && (
                    <div className="flexItem quotes-old">
                        <ImgTileLink src={`/${resourcesHelper.THEME}/img/quotes-icon.png`}
                                     alt="Quotes icon"
                                     to="/dashboard/viewOrGetQuotes"
                                     hasInfoButton={true}
                                     infoModalTitle="View / get new quote(s)"
                                     infoModalBulletPoints={["View your car or home insurance quote", "Get a new car or home insurance quote",
                                         "Get a new LV= travel or LV= pet insurance quote"]}
                                     infoModalButton="View / get new quote"
                                     {...props}>
                            View / get new quote(s)
                        </ImgTileLink>
                    </div>
                )}

                {hasPolicies && !hasMultiplePolicies && <div className="flexItem account-details">
                    <ImgTileLink src={`/${resourcesHelper.THEME}/img/account-details.png`}
                                 alt="Account details icon"
                                 to={`/dashboard/annualMotorAccount/${policyNumber}`}
                                 hasInfoButton={true}
                                 infoModalTitle="Account details"
                                 infoModalBulletPoints={["View and amend account holder details (e.g. email, phone number, address, password)",
                                     "Opt in to auto-renew your policy"]}
                                 {...props}>
                        Account details
                    </ImgTileLink>
                </div>}

                <div className="flexItem help">
                    <ImgTileLink src={`/${resourcesHelper.THEME}/img/help.png`}
                                 alt="Help icon"
                                 to={`https://www.allianz.co.uk/insurance/car-insurance/faqs`}
                                 openInNewWindow={true}
                                 hasInfoButton={true}
                                 infoModalTitle="Help"
                                 infoModalBulletPoints={["View 'How to' videos and guides",
                                     "Read FAQs about your cover, account, payment, breakdown, renewal, claims, NCD..."]}
                                 {...props}>
                        Help
                    </ImgTileLink>
                </div>

                {!inGracePeriod && hasPolicies && !policyCanceled && (<div className="flexItem cancel-policy">
                        {hasPendingRenewal ?
                            (
                                <ImgTileLink src={`/${resourcesHelper.THEME}/img/cancel-policy-icon.png`}
                                             alt="Cancel policy"
                                             to={`/dashboard/annualMotor/cancel/${policyNumber}`}
                                             routeState={{
                                                 isRenewalCancellation: true,
                                                 renewalCancellationUrl: '/journey/init/product/AnnualMotorInsurance/process/renewal/renewalMTCEntryPage/' + renewalProposal!._id
                                             }}
                                             hasInfoButton={true}
                                             infoModalTitle="Cancel policy"
                                             infoModalBulletPoints={["Cancel your policy"]}
                                             {...props}
                                >
                                    Cancel policy
                                </ImgTileLink>
                            ) : (
                                <ImgTileLink src={`/${resourcesHelper.THEME}/img/cancel-policy-icon.png`}
                                             alt="Cancel policy"
                                             to={`/journey/init/product/AnnualMotorInsurance/process/mtc/presetPage/${policy!.interaction?._id}`}
                                             hasInfoButton={true}
                                             infoModalTitle="Cancel policy"
                                             infoModalBulletPoints={["Cancel your policy"]}
                                             {...props}
                                >
                                    Cancel policy
                                </ImgTileLink>
                            )
                        }
                    </div>
                )}

                {(hasExpiredPolicies || hasOldPolicies) && (<div className="flexItem old-policies">
                        <ImgTileLink src={`/${resourcesHelper.THEME}/img/old-policies.png`}
                                     alt="Old policies"
                                     to={hasOldHomePolicies ? "/dashboard/annualOldPolicies" : "/dashboard/annualMotorOldPolicies"}
                                     hasInfoButton={true}
                                     infoModalTitle="Old policies"
                                     infoModalBulletPoints={["View your previous policies",
                                         "Make a claim on your previous policies"]}
                                     {...props}>
                            Old policies
                        </ImgTileLink>
                    </div>
                )}

                {hasActivePolicy && !allianzAdvantagesEnabled && !hasMultiplePolicies && <div className="flexItem savings">
                    <ImgTileLink src={`/${resourcesHelper.THEME}/img/gift_icon.svg`}
                                 alt="Offers icon"
                                 to={`https://www.allianz.co.uk/insurance/car-insurance/customer-offers`}
                                 openInNewWindow={true}
                                 hasInfoButton={true}
                                 infoModalTitle="My offers"
                                 infoModalBulletPointTitle="Our way of saying thanks"
                                 infoModalBulletPoints={["Save with exclusive offers from Allianz"]}
                                 {...props}>
                        My offers
                    </ImgTileLink>
                </div>}

                {hasActivePolicy && allianzAdvantagesEnabled && <div className="flexItem advantages">
                    <ImgTileLink id="getAdvantagesTile"
                                 src={`/${resourcesHelper.THEME}/img/allianz-star.svg`}
                                 alt="Allianz Advantages icon"
                                 to={`/dashboard/advantages`}
                                 routeState={{
                                     policy,
                                 }}
                                 infoModalBulletPointTitle={`Our way of saying thanks`}
                                 hasInfoButton={true}
                                 infoModalTitle="Allianz Advantages"
                                 infoModalBulletPoints={["Save with exclusive offers from Allianz"]}
                                 infoModalButton="Allianz Advantages"
                                 {...props}>
                        Allianz Advantages
                    </ImgTileLink>
                </div>}

                {hasPolicies && !hasMultiplePolicies && <div className="flexItem marketing-preferences">
                    <ImgTileLink src={`/${resourcesHelper.THEME}/img/exclamation-mark-dark.svg`}
                                 alt="Marketing preferences"
                                 to={"/dashboard/marketing-preferences"}
                                 hasInfoButton={true}
                                 infoModalTitle="Marketing preferences"
                                 infoModalBulletPoints={["Change your marketing preferences (hear about our latest products, services and discounts"]}
                                 {...props}>
                        Marketing preferences
                    </ImgTileLink>
                </div>}

            </div>
        </div>
    );
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/annual/motor/dashboardPage/AnnualTiles.tsx